import * as tslib_1 from "tslib";
import { ElementRef, ErrorHandler, EventEmitter, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { tap, debounceTime, distinctUntilChanged, switchMap, catchError, map } from 'rxjs/operators';
import { mergeStates, State } from '../state-view/state.model';
import { AutocompleteOptionType, AutocompleteType, COUNTRIES } from '../autocomplete-trigger/autocomplete-trigger.component';
import { AirportService } from '../../airport/airport.service';
import { GoogleService } from '../../google/google.service';
import { STATES } from '../../util/states';
import { isString } from '../../util/helpers';
import { Source } from '../models/source.model';
import { SearchFormType } from '../../search/models/search-form-type.model';
var AutocompleteOverlayComponent = /** @class */ (function () {
    function AutocompleteOverlayComponent(_router, _airportService, _googleService, _errorHandler) {
        this._router = _router;
        this._airportService = _airportService;
        this._googleService = _googleService;
        this._errorHandler = _errorHandler;
        this.active = false;
        this.queryType = AutocompleteType.Airports;
        this.international = false;
        this.currentSearchTab = SearchFormType.Airports;
        this.itemSelected = new EventEmitter();
        this.State = State;
        this.airportSearchState = State.Empty;
        this.locationState = State.Empty;
        this.airportState = State.Empty;
        this.googleState = State.Empty;
        this.form = new FormGroup({
            wrapper: new FormGroup({
                type: new FormControl(),
                query: new FormControl()
            })
        });
        this.isString = isString;
        this.searchFormType = SearchFormType;
    }
    Object.defineProperty(AutocompleteOverlayComponent.prototype, "searchState", {
        get: function () {
            return mergeStates(this.airportSearchState, this.locationState);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteOverlayComponent.prototype, "queryCtrl", {
        get: function () {
            return this.form.get('wrapper').get('query');
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteOverlayComponent.prototype, "showAirports", {
        get: function () {
            return (this.queryType === AutocompleteType.Airports || this.queryType === AutocompleteType.Combined) && !this.international;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteOverlayComponent.prototype, "showGoogle", {
        get: function () {
            return (this.queryType === AutocompleteType.Google || this.queryType === AutocompleteType.Combined) && !this.international;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteOverlayComponent.prototype, "showInternational", {
        get: function () {
            return this.queryType === AutocompleteType.Combined && this.international;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteOverlayComponent.prototype, "sanitizedQuery", {
        get: function () {
            return AirportService.sanitizeSearchTerm(this.query);
        },
        enumerable: true,
        configurable: true
    });
    AutocompleteOverlayComponent.prototype.ngOnInit = function () {
        var _this = this;
        this.searchTerm$ = this.queryCtrl.valueChanges.pipe(
        // wait 300ms after each keystroke before considering the term
        debounceTime(300), 
        // ignore if next search term is same as previous
        distinctUntilChanged());
        if (this.queryType === AutocompleteType.Airports || this.queryType === AutocompleteType.Combined) {
            this.airportResults$ = this.searchTerm$.pipe(tap(function () {
                _this.airportState = State.Loading;
                _this._updateState();
            }), switchMap(function (query) { return _this.queryType === AutocompleteType.Combined
                ? _this._airportService.getAirportCompletions(query, 5)
                : _this._airportService.getAirportCompletions(query); }), tap(function () {
                _this.airportState = State.Content;
                _this._updateState();
            }), catchError(function (e) {
                _this._errorHandler.handleError(e);
                _this.airportState = State.Error;
                _this._updateState();
                return of([]);
            }));
        }
        if (this.queryType === AutocompleteType.Google || this.queryType === AutocompleteType.Combined) {
            this.googleResults$ = this.searchTerm$.pipe(tap(function () {
                _this.googleState = State.Loading;
                _this._updateState();
            }), switchMap(function (query) {
                if (!query) {
                    return of([]);
                }
                return _this._googleService.getPlacePredictions({
                    input: query,
                    types: ['(cities)'],
                    componentRestrictions: {
                        country: COUNTRIES
                    }
                });
            }), tap(function () {
                _this.googleState = State.Content;
                _this._updateState();
            }), catchError(function (e) {
                _this._errorHandler.handleError(e);
                _this.googleState = State.Error;
                _this._updateState();
                return of([]);
            }));
            // to undo, put after types above
            // sessionToken: new google.maps.places.AutocompleteSessionToken(),
        }
        if (this.queryType === AutocompleteType.Combined) {
            this.usStateResults$ = this.searchTerm$.pipe(map(function (query) {
                if (_this.queryType === AutocompleteType.Combined && isString(query)) {
                    return _this._addUSStates(query);
                }
            }), catchError(function (e) {
                _this._errorHandler.handleError(e);
                _this.googleState = State.Error;
                _this._updateState();
                return of([]);
            }));
        }
        if (this.queryType === AutocompleteType.Combined) {
            this.internationalCityResults$ = this.searchTerm$.pipe(tap(function () {
                _this.googleState = State.Loading;
                _this._updateState();
            }), switchMap(function (query) {
                var intResult = _this._googleService.getPlacePredictions({
                    input: query ? query : ' ',
                    types: ['(regions)']
                });
                var intResult2 = intResult.then(function (intValues) { return intValues.filter(function (item) { return !item.description.endsWith(', USA'); }).filter(function (item2) { return item2.types.includes('locality') || item2.types.includes('country'); }).sort(function (a, b) {
                    if (a.types.includes('locality') && !b.types.includes('locality')) {
                        return -1;
                    }
                    if (a.types.includes('country') && !b.types.includes('country')) {
                        return 1;
                    }
                    if (a.description < b.description) {
                        return -1;
                    }
                    if (a.description > b.description) {
                        return 1;
                    }
                    return 0;
                }); });
                return intResult2;
            }), tap(function () {
                _this.googleState = State.Content;
                _this._updateState();
            }), catchError(function (e) {
                _this._errorHandler.handleError(e);
                _this.googleState = State.Error;
                _this._updateState();
                return of([]);
            }));
        }
    };
    AutocompleteOverlayComponent.prototype._addUSStates = function (query) {
        var filteredStates = [];
        if (query.length < 2) {
            return filteredStates;
        }
        if (query.length === 2) {
            filteredStates = STATES.filter(function (item) { return item.stateCode.toLowerCase().startsWith(query.toLowerCase()); });
        }
        else {
            filteredStates = STATES.filter(function (item) { return item.name.toLowerCase().startsWith(query.toLowerCase()); });
        }
        return filteredStates;
    };
    AutocompleteOverlayComponent.prototype.show = function () {
        this.airportSearchState = State.Empty;
        this.locationState = State.Empty;
        this.queryCtrl.setValue('');
        this.active = true;
    };
    AutocompleteOverlayComponent.prototype.focus = function () {
        this.queryFieldEl.nativeElement.focus();
    };
    AutocompleteOverlayComponent.prototype.hide = function () {
        this.queryFieldEl.nativeElement.blur();
        this.active = false;
    };
    AutocompleteOverlayComponent.prototype.onQueryBlur = function () {
        var _this = this;
        setTimeout(function () { return _this.hide(); }, 300);
    };
    AutocompleteOverlayComponent.prototype._updateState = function () {
        this.state = mergeStates(this.airportState, this.googleState);
    };
    AutocompleteOverlayComponent.prototype.getAirportText = function (result) {
        return (result.icaoId || result.faaId || '') + ": " + result.name;
    };
    AutocompleteOverlayComponent.prototype.getCityStateText = function (result) {
        var city = result.city;
        var state = result.stateProvince;
        if (city || state) {
            return '(' + [city, state].filter(function (v) { return v; }).join(', ') + ')';
        }
        return '';
    };
    AutocompleteOverlayComponent.prototype.getCityText = function () {
        if (this.queryType === AutocompleteType.Combined) {
            return ' (City)';
        }
        return '';
    };
    AutocompleteOverlayComponent.prototype.getStateTerritoryText = function () {
        return ' (State/Territory)';
    };
    AutocompleteOverlayComponent.prototype.getInternationalText = function (result) {
        if (result.types.includes('country')) {
            return ' (Country)';
        }
        if (result.types.includes('locality')) {
            return ' (City)';
        }
        return '';
    };
    AutocompleteOverlayComponent.prototype.onSelectAirport = function (airport) {
        this.itemSelected.emit({
            type: AutocompleteOptionType.Airport,
            airport: airport
        });
        this.hide();
    };
    AutocompleteOverlayComponent.prototype.onSelectLocation = function (location) {
        this.itemSelected.emit({
            type: AutocompleteOptionType.Location,
            location: location
        });
        this.hide();
    };
    AutocompleteOverlayComponent.prototype.onSelectUSState = function (state) {
        this.itemSelected.emit({
            type: AutocompleteOptionType.State,
            state: state
        });
        this.hide();
    };
    AutocompleteOverlayComponent.prototype.onSubmit = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var term, result, e_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        term = this.sanitizedQuery.toUpperCase();
                        if (!term) return [3 /*break*/, 4];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        this.state = State.Loading;
                        return [4 /*yield*/, this._airportService.getAirport(term)];
                    case 2:
                        result = _a.sent();
                        if (result) {
                            if (result.sourceId === Source.CaribbeanGuide || result.sourceId === Source.BahamasGuide) {
                                this._router.navigate(['/airports', term, 'island']);
                            }
                            else {
                                this._router.navigate(['/airports', term]);
                            }
                            this.hide();
                            return [2 /*return*/];
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        this._errorHandler.handleError(e_1);
                        this.state = State.Error;
                        return [2 /*return*/];
                    case 4:
                        this.itemSelected.emit({
                            type: AutocompleteOptionType.Text,
                            query: this.sanitizedQuery
                        });
                        this.hide();
                        return [2 /*return*/];
                }
            });
        });
    };
    return AutocompleteOverlayComponent;
}());
export { AutocompleteOverlayComponent };
